import { createSelector } from '@reduxjs/toolkit';
import { makeValuesPredicate } from '@sgme/fp';

import { BulkOrderStatus, type BulkStateData } from '@/store/bulkOrder/state.ts';
import type { AppState } from '@/store/store.ts';

const selectSelf = (state: AppState, tabId: string, _params?: { orderId: string }): BulkStateData | undefined => {
  return state.orderBulks?.[tabId];
};

export const stopLossBulkOrderIdsSelector = createSelector([selectSelf], (state) => state?.stopLoss?.orderIds);
export const fixingBulkOrderIdsSelector = createSelector([selectSelf], (state) => state?.fixing?.orderIds);

export const allFixingBulkOrdersSelector = createSelector([selectSelf], (state) =>
  Object.values(state?.fixing?.orderById || {}),
);
export const allStopLossBulkOrdersSelector = createSelector([selectSelf], (state) =>
  Object.values(state?.stopLoss?.orderById || {}),
);

export const stopLossBulkOrderSelector = createSelector(
  [selectSelf, (_state, _tabId: string, params: { orderId: string }) => params.orderId],
  (state, orderId) => state?.stopLoss?.orderById[orderId],
);

export const fixingBulkOrderSelector = createSelector(
  [selectSelf, (_state, _tabId: string, params: { orderId: string }) => params.orderId],
  (state, orderId) => state?.fixing?.orderById[orderId],
);

const bulkOrderPendingStatuses = [
  BulkOrderStatus.VALIDATING,
  BulkOrderStatus.INVALID,
  BulkOrderStatus.PENDING,
  BulkOrderStatus.FAILED,
];
const isBulkOrderPending = makeValuesPredicate(bulkOrderPendingStatuses);

export const stopLossBulkExecutionStatusSelector = createSelector([selectSelf], (state) =>
  Object.values(state?.stopLoss?.orderById || {}).reduce((status, order) => {
    if (isBulkOrderPending(status)) {
      return status;
    }

    if (isBulkOrderPending(order?.properties?.status)) {
      return order?.properties?.status;
    }

    if (
      (status === BulkOrderStatus.VALID || status === BulkOrderStatus.SUCCESS) &&
      order?.properties?.status === BulkOrderStatus.SUCCESS
    ) {
      return BulkOrderStatus.SUCCESS;
    }

    return status;
  }, BulkOrderStatus.VALID),
);

export const fixingBulkExecutionStatusSelector = createSelector([selectSelf], (state) =>
  Object.values(state?.fixing?.orderById || {}).reduce((status, order) => {
    if (isBulkOrderPending(status)) {
      return status;
    }

    if (isBulkOrderPending(order?.properties?.status)) {
      return order?.properties?.status;
    }

    if (
      (status === BulkOrderStatus.VALID || status === BulkOrderStatus.SUCCESS) &&
      order?.properties?.status === BulkOrderStatus.SUCCESS
    ) {
      return BulkOrderStatus.SUCCESS;
    }

    return status;
  }, BulkOrderStatus.VALID),
);
